import Analytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useHistory, useParams } from 'react-router-dom';

import { logoutUser } from 'connect/actions';
import { RequestPatientPhoneVerification, VerifyPatientPhone } from 'connect/saga/handlers/patient/unverified-phone';
import {
  RequestEmailVerification,
  RequestPhoneVerification,
  RequestPhoneVerificationSmsStatus,
  VerifyEmail,
  VerifyPhone,
} from 'connect/saga/handlers/patient/verify';
import { BodyText, CodeInputStateWrapper, CodeSection, InstructionsWrapper } from 'pages/checkout/components/codeForm/CodeForm-styled-components';
import { CheckoutLinkButton, FormError } from 'pages/checkout/components/common/input-styled-components';
import { StepHeader, StepHeaderSubtext, StepHeaderText } from 'pages/checkout/components/common/layout-styled-components';
import { ResendCodeText, ResendCodeWrapper } from 'pages/checkout/components/ResendCode/ResendCode-styled-components';
import { resetCheckoutData } from 'pages/checkout/connect/helpers';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { CheckoutState } from 'pages/checkout/connect/types';
import { useCheckoutBaseUrl, useIsEmailVerificationSkipped, useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { formatPhoneNumber } from 'pages/checkout/utils/phoneUtils';

import { useAnalyticsService } from 'services/AnalyticsService';
import { useCheckoutPatient } from 'utils/customHooks/use-checkout-patient';
import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import { useEffectOnce as useEffectOnce } from 'utils/customHooks/useEffectOnce';

import { usePatch } from 'utils/customHooks/usePatch';
import { formatErrorMessage } from 'utils/errorMessage';
import Logger from 'utils/Logger';

import { stepDefinitions } from '../stepDefinitions';
import { StepId } from '../types';

import { StateIndicator } from './StateIndicator';
import { CodeInputState } from './types';
import { CodeInput, ContactText, InputWrapper, VerifyStepWrapper } from './Verify-styled-components';

const analytics = new Analytics();

const PHONE_ALREADY_VERIFIED_ERROR = 'This patient phone number has already been verified.';
const EMAIL_ALREADY_VERIFIED_ERROR = 'This patient email is already verified.';

const GENERIC_ERROR_MESSAGE = 'Something went wrong on our end, try resending the code.';

const ResendCodeContent = {
  [CodeInputState.Phone]: 'Resend Message',
  [CodeInputState.Email]: 'Send a new email',
};

const PHONE_VERIFICATION_ERROR_MESSAGES = {
  'The phone verification code specified was invalid.': 'The code you entered is incorrect.',
  'The phone verification code specified has been attempted too many times.': 'You have made too many incorrect attempts. Please request a new code.',
  'The phone verification code specified is too old.': 'The code you entered has expired. Please request a new code.',
};

const EMAIL_VERIFICATION_ERROR_MESSAGES = {
  'The email verification code specified is wrong.': 'The code you entered is incorrect.',
};

export const Verify = () => {
  const dispatch = useDispatch();
  const analyticsService = useAnalyticsService();

  const isPartnerUser = useIsPartnerUser();
  const patient = useCheckoutPatient();

  const { email, mobilePhone: phoneNumber } = useMemo(() => patient || {}, [patient]);

  const { clinicId } = useParams<{ clinicId: string; }>();
  const history = useHistory();

  const [codeInputState, setCodeInputState] = useState<CodeInputState>(CodeInputState.Phone);
  const [phoneCode, setPhoneCode] = useState<string>('');
  const [emailCode, setEmailCode] = useState<string>('');
  const [smsDeliveryFailed, setSmsDeliveryFailed] = useState(false);

  const [showResendPhoneCode, setShowResendPhoneCode] = useState(false);
  const [showResendEmailCode, setShowResendEmailCode] = useState(false);

  const [resendCooldown, setResendCooldown] = useState(0);

  const isEmailVerificationSkipped = useIsEmailVerificationSkipped();

  useEffect(() => {
    const showCodeTimeout = setTimeout(() => {
      if (codeInputState === CodeInputState.Phone) {
        setShowResendPhoneCode(true);
      } else {
        setShowResendEmailCode(true);
      }
    }, 5000);

    return () => clearTimeout(showCodeTimeout);
  }, [codeInputState]);

  const baseUrl = useCheckoutBaseUrl();
  const { goToNextStep } = useNextCheckoutStep();

  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const account = useMemo(() => checkoutState?.account || {}, [checkoutState]);
  const phoneToUpdate = useMemo(() => account.phoneToUpdate, [account]);

  const shouldUseUnverifiedPhone = useMemo(
    () => phoneToUpdate && phoneToUpdate !== patient?.mobilePhone,
    [phoneToUpdate, patient?.mobilePhone],
  );

  const phoneAlreadyVerified = useMemo(
    () => patient?.phoneVerifiedAt && !shouldUseUnverifiedPhone,
    [patient?.phoneVerifiedAt, shouldUseUnverifiedPhone],
  );

  const contactMethodsToVerify = useMemo(() => {
    const methods = new Set<CodeInputState>([CodeInputState.Phone, CodeInputState.Email]);

    if (patient?.emailVerifiedAt || isEmailVerificationSkipped) {
      methods.delete(CodeInputState.Email);
    }

    if (phoneAlreadyVerified) {
      methods.delete(CodeInputState.Phone);
    }

    return Array.from(methods);
  }, [patient?.emailVerifiedAt, phoneAlreadyVerified, isEmailVerificationSkipped]);

  const requestUnverifiedPhoneVerificationLoading = useSelector(RequestPatientPhoneVerification.loading!.selector);
  const requestUnverifiedPhoneVerificationError = useSelector(RequestPatientPhoneVerification.error!.selector);
  const requestUnverifiedPhoneVerificationSuccess = useSelector(RequestPatientPhoneVerification.success!.selector);

  const unverifiedPhoneVerificationLoading = useSelector(VerifyPatientPhone.loading!.selector);
  const unverifiedPhoneVerificationError = useSelector(VerifyPatientPhone.error!.selector);
  const unverifiedPhoneVerificationSuccess = useSelector(VerifyPatientPhone.success!.selector);

  const mobilePhoneVerificationLoading = useSelector(VerifyPhone.loading!.selector);
  const mobilePhoneVerificationError = useSelector(VerifyPhone.error!.selector);
  const mobilePhoneVerificationSuccess = useSelector(VerifyPhone.success!.selector);

  const phoneVerificationLoading = useMemo(
    () => shouldUseUnverifiedPhone
      ? unverifiedPhoneVerificationLoading
      : mobilePhoneVerificationLoading,
    [shouldUseUnverifiedPhone, unverifiedPhoneVerificationLoading, mobilePhoneVerificationLoading],
  );

  const phoneVerificationError = useMemo(
    () => shouldUseUnverifiedPhone
      ? unverifiedPhoneVerificationError
      : mobilePhoneVerificationError,
    [shouldUseUnverifiedPhone, unverifiedPhoneVerificationError, mobilePhoneVerificationError],
  );

  const phoneVerificationSuccess = useMemo(
    () => shouldUseUnverifiedPhone
      ? unverifiedPhoneVerificationSuccess
      : mobilePhoneVerificationSuccess,
    [shouldUseUnverifiedPhone, unverifiedPhoneVerificationSuccess, mobilePhoneVerificationSuccess],
  );

  const emailVerificationLoading = useSelector(VerifyEmail.loading!.selector);
  const emailVerificationError = useSelector(VerifyEmail.error!.selector);
  const emailVerificationSuccess = useSelector(VerifyEmail.success!.selector);

  const requestMobilePhoneVerificationLoading = useSelector(RequestPhoneVerification.loading!.selector);
  const requestMobilePhoneVerificationError = useSelector(RequestPhoneVerification.error!.selector);
  const requestMobilePhoneVerificationSuccess = useSelector(RequestPhoneVerification.success!.selector);

  const requestPhoneLoading = useMemo(
    () => shouldUseUnverifiedPhone
      ? requestUnverifiedPhoneVerificationLoading
      : requestMobilePhoneVerificationLoading,
    [shouldUseUnverifiedPhone, requestMobilePhoneVerificationLoading, requestUnverifiedPhoneVerificationLoading],
  );

  const requestPhoneError = useMemo(
    () => shouldUseUnverifiedPhone
      ? requestUnverifiedPhoneVerificationError
      : requestMobilePhoneVerificationError,
    [shouldUseUnverifiedPhone, requestMobilePhoneVerificationError, requestUnverifiedPhoneVerificationError],
  );

  const requestPhoneSuccess = useMemo(
    () => shouldUseUnverifiedPhone
      ? requestUnverifiedPhoneVerificationSuccess
      : requestMobilePhoneVerificationSuccess,
    [shouldUseUnverifiedPhone, requestMobilePhoneVerificationSuccess, requestUnverifiedPhoneVerificationSuccess],
  );

  const requestEmailLoading = useSelector(RequestEmailVerification.loading!.selector);
  const requestEmailError = useSelector(RequestEmailVerification.error!.selector);

  const smsStatusLoading = useSelector(RequestPhoneVerificationSmsStatus.loading!.selector);
  const smsStatusValue = useSelector(RequestPhoneVerificationSmsStatus.value!.selector);

  useEffectOnce(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify',
      ...analyticsService.analyticsData,
    });
  });

  useEffect(() => {
    if (codeInputState === CodeInputState.Phone) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: 'checkout-verify',
        name: 'checkout',
        section: 'verify-phone',
        ...analyticsService.analyticsData,
      });
    }

    if (codeInputState === CodeInputState.Email) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.CHECKOUT,
        pageName: 'checkout-verify',
        name: 'checkout',
        section: 'verify-email',
        ...analyticsService.analyticsData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every render, so we don't want it in the dependency array
  }, [codeInputState]);

  const handleEmailVerifySuccess = useCallback(() => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify-email',
      content: {
        status: 'success',
      },
    });

    dispatch(VerifyEmail.success!.update(null));
    goToNextStep({ replace: true });
  }, [dispatch, goToNextStep]);

  const handleSendEmailCode = useCallback(() => {
    const alreadyVerified = patient?.emailVerifiedAt;
    if (alreadyVerified) {
      handleEmailVerifySuccess();
      return;
    }

    dispatch(RequestEmailVerification.createAction());
    dispatch(VerifyEmail.reset());
  }, [dispatch, handleEmailVerifySuccess, patient?.emailVerifiedAt]);

  const patchCheckout = usePatch<CheckoutState>({ stateHandler: CheckoutStateHandler });

  const handlePhoneVerifySuccess = useCallback(() => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'verify-phone',
      content: {
        status: 'success',
      },
    });

    patchCheckout({
      account: {
        phoneToUpdate: null,
      },
    });
    dispatch(VerifyPatientPhone.reset());
    dispatch(VerifyPhone.success!.update(null));

    const shouldVerifyEmail = contactMethodsToVerify.includes(CodeInputState.Email);

    if (shouldVerifyEmail) {
      handleSendEmailCode();
      setCodeInputState(CodeInputState.Email);
      return;
    }

    goToNextStep({ replace: true });
  }, [
    dispatch,
    handleSendEmailCode,
    patchCheckout,
    goToNextStep,
    contactMethodsToVerify,
  ]);

  const handleSendPhoneCode = useCallback(() => {
    if (phoneAlreadyVerified) {
      handlePhoneVerifySuccess();
      return;
    }

    if (shouldUseUnverifiedPhone) {
      dispatch(RequestPatientPhoneVerification.createAction({ phone: phoneToUpdate }));
      dispatch(VerifyPatientPhone.reset());
      return;
    }

    dispatch(RequestPhoneVerification.createAction('text'));
    dispatch(VerifyPhone.reset());
  }, [dispatch, handlePhoneVerifySuccess, phoneAlreadyVerified, shouldUseUnverifiedPhone, phoneToUpdate]);

  const resendCooldownInterval = useRef<NodeJS.Timeout | null>(null);

  const handleSendCode = useCallback(() => {
    // Request verification requires patient id
    if (!patient?.id) {
      return;
    }

    setPhoneCode('');
    setEmailCode('');

    if (codeInputState === CodeInputState.Phone) {
      handleSendPhoneCode();
    }

    if (codeInputState === CodeInputState.Email) {
      handleSendEmailCode();
    }
  }, [codeInputState, handleSendPhoneCode, handleSendEmailCode, patient?.id]);

  const handleResendCode = useCallback(() => {
    if (resendCooldown > 0) {
      return;
    }

    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: codeInputState === CodeInputState.Phone ? 'verify-phone' : 'verify-email',
      content: {
        action: 'resend-code',
      },
    });

    handleSendCode();

    if (resendCooldownInterval.current) {
      clearInterval(resendCooldownInterval.current);
    }

    setResendCooldown(60);
    resendCooldownInterval.current = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
  }, [handleSendCode, resendCooldown, codeInputState]);

  // Send code on mount if patient id is present
  useEffectOnce(handleSendCode, Boolean(patient?.id));

  // Check SMS delivery status
  useEffect(() => {
    if (codeInputState !== CodeInputState.Phone || smsStatusLoading) {
      return;
    }

    // Ignore SMS status if we're using unverified phone
    if (requestPhoneSuccess && !shouldUseUnverifiedPhone) {
      setSmsDeliveryFailed(false);
      dispatch(RequestPhoneVerification.success!.update(null));
      dispatch(RequestPhoneVerificationSmsStatus.createAction());
    }
  }, [codeInputState, requestPhoneSuccess, shouldUseUnverifiedPhone, smsStatusLoading, dispatch]);

  // Handle SMS status response
  useEffect(() => {
    if (smsStatusValue && smsStatusValue.status === 'failed') {
      setSmsDeliveryFailed(true);
      dispatch(RequestPhoneVerification.createAction('call'));
    }
  }, [smsStatusValue, dispatch]);

  const isSendingCode = useMemo(() => {
    if (codeInputState === CodeInputState.Phone) {
      return requestPhoneLoading;
    }

    if (codeInputState === CodeInputState.Email) {
      return requestEmailLoading;
    }

    return false;
  }, [
    codeInputState,
    requestPhoneLoading,
    requestEmailLoading,
  ]);

  const isLoadingVerification = useMemo(() => {
    if (codeInputState === CodeInputState.Phone) {
      return phoneVerificationLoading;
    }

    if (codeInputState === CodeInputState.Email) {
      return emailVerificationLoading;
    }

    return false;
  }, [codeInputState, phoneVerificationLoading, emailVerificationLoading]);

  const errorMessage = useMemo(() => {
    if (codeInputState === CodeInputState.Phone && phoneVerificationError) {
      return formatErrorMessage({
        errorMessage: phoneVerificationError,
        defaultErrorMessage: GENERIC_ERROR_MESSAGE,
        errorMessageMap: PHONE_VERIFICATION_ERROR_MESSAGES,
      });
    }

    if (codeInputState === CodeInputState.Email && emailVerificationError) {
      return formatErrorMessage({
        errorMessage: emailVerificationError,
        defaultErrorMessage: GENERIC_ERROR_MESSAGE,
        errorMessageMap: EMAIL_VERIFICATION_ERROR_MESSAGES,
      });
    }

    return null;
  }, [codeInputState, phoneVerificationError, emailVerificationError]);

  // Clear code input on error
  useEffect(() => {
    if (errorMessage) {
      setPhoneCode('');
      setEmailCode('');
    }
  }, [errorMessage]);

  // If patient does not have a phone number in Redux, or there is an error about not having a phone number, redirect to phone number step
  useEffect(() => {
    const isMissingPhoneError = requestPhoneError === 'This patient doesn\'t have phone number.';
    if (patient?.mobilePhone && !isMissingPhoneError) {
      return;
    }

    Logger.error('Patient reached verify step without a phone number. Redirecting to phone number step.', {
      patientId: patient?.id,
    });
    const phoneNumberStep = stepDefinitions.find((step) => step.id === StepId.PHONE_NUMBER);
    history.replace(`/results/${clinicId}/checkout/${phoneNumberStep?.path}`);
  }, [patient?.id, patient?.mobilePhone, requestPhoneError, clinicId, history]);

  // Handle success state
  useEffect(() => {
    if (isLoadingVerification) {
      return;
    }

    if (codeInputState === CodeInputState.Phone) {
      if (shouldUseUnverifiedPhone && phoneVerificationSuccess) {
        return handlePhoneVerifySuccess();
      }

      const alreadyVerified = phoneAlreadyVerified
        || phoneVerificationError === PHONE_ALREADY_VERIFIED_ERROR;

      if (phoneVerificationSuccess || alreadyVerified) {
        handlePhoneVerifySuccess();
      }

      return;
    }

    if (codeInputState === CodeInputState.Email) {
      const alreadyVerified = patient?.emailVerifiedAt
        || [emailVerificationError, requestEmailError].includes(EMAIL_ALREADY_VERIFIED_ERROR);

      if (emailVerificationSuccess || alreadyVerified) {
        handleEmailVerifySuccess();
      }
    }
  }, [
    phoneVerificationSuccess,
    emailVerificationSuccess,
    codeInputState,
    phoneVerificationError,
    emailVerificationError,
    dispatch,
    requestEmailError,
    patient,
    phoneVerificationLoading,
    emailVerificationLoading,
    handlePhoneVerifySuccess,
    handleEmailVerifySuccess,
    isLoadingVerification,
    phoneAlreadyVerified,
    shouldUseUnverifiedPhone,
    unverifiedPhoneVerificationSuccess,
  ]);

  const handleSubmitCode = (code: string) => {
    analytics.trackForm({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: codeInputState === CodeInputState.Phone ? 'verify-phone' : 'verify-email',
      content: {
        action: 'submit-code',
      },
    });

    const actionToDispatch = (() => {
      if (codeInputState === CodeInputState.Phone) {
        if (shouldUseUnverifiedPhone) {
          return VerifyPatientPhone.createAction({ phone: phoneToUpdate, code });
        }

        return VerifyPhone.createAction(code);
      }

      if (codeInputState === CodeInputState.Email) {
        return VerifyEmail.createAction(code);
      }

      Logger.error('Unknown code input state, cannot submit code', {
        codeInputState,
      });

      return null;
    })();

    dispatch(actionToDispatch);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberValue = Number(value);

    const setCode = codeInputState === CodeInputState.Phone ? setPhoneCode : setEmailCode;

    if (!value || Number.isNaN(numberValue)) {
      return;
    }

    if (value.length <= 4) {
      setCode(value);
    }

    if (value.length === 4) {
      handleSubmitCode(value);
    }
  };

  // The fallbacks should never be needed, but just in case
  const formattedPhoneNumber = useMemo(
    () => formatPhoneNumber(shouldUseUnverifiedPhone ? phoneToUpdate : phoneNumber) || 'your phone',
    [shouldUseUnverifiedPhone, phoneToUpdate, phoneNumber],
  );
  const formattedEmail = useMemo(
    () => email || 'your email',
    [email],
  );

  const codeDestination = useMemo(
    () => ({
      [CodeInputState.Phone]: formattedPhoneNumber,
      [CodeInputState.Email]: formattedEmail,
    }[codeInputState]),
    [codeInputState, formattedPhoneNumber, formattedEmail],
  );

  const showResendCode = useMemo(() => {
    if (errorMessage) {
      return true;
    }

    return codeInputState === CodeInputState.Phone
      ? showResendPhoneCode
      : showResendEmailCode;
  }, [codeInputState, showResendPhoneCode, showResendEmailCode, errorMessage]);

  const isLoadingOrSendingCode = useMemo(
    () => isLoadingVerification || isSendingCode,
    [isLoadingVerification, isSendingCode],
  );

  const handleLogout = useCallback(() => {
    analytics.trackClick({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-verify',
      name: 'checkout',
      section: 'change-phone-number',
    });

    resetCheckoutData(dispatch);
    dispatch(logoutUser());
    history.push(baseUrl);

  }, [dispatch, history, baseUrl]);

  return (
    <VerifyStepWrapper>
      <StepHeader>
        <StepHeaderText>
          {isPartnerUser ? "Keep patient's account secure" : 'Keep your account secure'}
        </StepHeaderText>
        <StepHeaderSubtext>
          {isPartnerUser
            ? "Verify patient's phone to ensure they can access their account."
            : `To ensure you're the only one who can access your account, we need to verify your ${contactMethodsToVerify.join(' and ')}.`}
        </StepHeaderSubtext>
      </StepHeader>

      <CodeSection>
        <CodeInputStateWrapper>
          <StateIndicator codeInputState={codeInputState} contactMethodsToVerify={contactMethodsToVerify} />

          <InstructionsWrapper>
            <BodyText>Enter the code we sent to:</BodyText>

            <ContactText>{codeDestination}</ContactText>
          </InstructionsWrapper>
        </CodeInputStateWrapper>

        <InputWrapper>
          <CodeInput
            value={codeInputState === CodeInputState.Phone ? phoneCode : emailCode}
            onChange={handleCodeChange}
            $loading={isLoadingOrSendingCode}
          />

          {errorMessage && <FormError>{errorMessage}</FormError>}

          {smsDeliveryFailed && codeInputState === CodeInputState.Phone
            && (
              <BodyText>
                We have a delivery issue.
                Please expect a verification call within the next 5 seconds.
              </BodyText>
            )}
        </InputWrapper>

      </CodeSection>

      {showResendCode
        && (
          <ResendCodeWrapper>
            <ResendCodeText>
              <span>
                {isPartnerUser ? "Patient didn't receive anything? " : "Didn't receive anything? "}
              </span>
              <CheckoutLinkButton onClick={handleResendCode} disabled={isLoadingVerification || resendCooldown > 0}>
                {codeInputState === CodeInputState.Phone && smsDeliveryFailed
                  ? 'Call me again'
                  : ResendCodeContent[codeInputState]}
                {resendCooldown > 0 && ` (${resendCooldown}s)`}
              </CheckoutLinkButton>
            </ResendCodeText>

            {codeInputState === CodeInputState.Email
              && <BodyText>Remember to check your spam folder</BodyText>}
          </ResendCodeWrapper>
        )}

      {(shouldUseUnverifiedPhone && codeInputState === CodeInputState.Phone) && (
        <CheckoutLinkButton onClick={handleLogout}>
          Try a different number
        </CheckoutLinkButton>
      )}

    </VerifyStepWrapper>
  );
};
